import React, { useMemo, useState } from "react";
import { Button, Checkbox, Divider, Input, Popover } from "antd";
import { CheckOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { AnalyticsValidationRecord } from "../../services/analyticsValidationService";

export type FilterDimension = "platforms" | "channels" | "territories";

export interface AnalyticsFilterSelection {
	platforms: string[];
	channels: string[];
	territories: string[];
}

const isAllSelected = (selected: string[], options: SelectOption[]) =>
	selected.length === 0 || options.every(option => selected.includes(option.value));

interface SelectOption {
	value: string;
	label: string;
}

const DIMENSION_COLOR: Record<FilterDimension, string> = {
	platforms: "#1677ff",
	channels: "#389e0d",
	territories: "#d46b08",
};

const Bar = styled.div`
	padding: 4px 0 14px;
	margin-bottom: 8px;
	border-bottom: 1px solid #f0f0f0;
`;

const Row = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 10px;

	& + & {
		margin-top: 10px;
	}
`;

const RowLabel = styled.div<{ $color: string }>`
	flex: 0 0 92px;
	min-height: 24px;
	line-height: 24px;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: ${props => props.$color};
`;

const Chips = styled.div`
	flex: 1 1 auto;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
`;

const Chip = styled.span<{ $color: string }>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	height: 24px;
	padding: 0 8px;
	font-size: 12px;
	line-height: 22px;
	border-radius: 4px;
	color: ${props => props.$color};
	border: 1px solid ${props => props.$color}66;
	background: ${props => props.$color}0f;

	.chip-close {
		font-size: 12px;
		cursor: pointer;
		opacity: 0.7;

		&:hover {
			opacity: 1;
		}
	}
`;

const AddChip = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 24px;
	min-width: 26px;
	padding: 0 6px;
	font-size: 11px;
	color: rgba(0, 0, 0, 0.45);
	background: transparent;
	border: 1px dashed #d9d9d9;
	border-radius: 4px;
	cursor: pointer;

	&:hover {
		color: #1677ff;
		border-color: #1677ff;
	}
`;

const ClearLink = styled.button`
	height: 24px;
	padding: 0 2px;
	font-size: 12px;
	color: rgba(0, 0, 0, 0.55);
	text-decoration: underline;
	background: transparent;
	border: none;
	cursor: pointer;

	&:hover {
		color: #1677ff;
	}
`;

const Summary = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	font-size: 12px;
	color: rgba(0, 0, 0, 0.65);

	.summary-label {
		font-weight: 600;
		letter-spacing: 0.04em;
		text-transform: uppercase;
	}

	.summary-dot {
		color: rgba(0, 0, 0, 0.25);
	}
`;

interface FilterPopoverContentProps {
	options: SelectOption[];
	selected: string[];
	onChange: (values: string[]) => void;
}

const FilterPopoverContent: React.FC<FilterPopoverContentProps> = ({
	options,
	selected,
	onChange,
}) => {
	const [search, setSearch] = useState("");

	const visibleOptions = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return options;
		return options.filter(option => option.label.toLowerCase().includes(query));
	}, [options, search]);

	const toggleValue = (value: string, checked: boolean) => {
		if (checked) {
			onChange([...selected, value]);
			return;
		}
		onChange(selected.filter(item => item !== value));
	};

	return (
		<div style={{ width: 270 }}>
			<Input
				allowClear
				size="small"
				placeholder="Search"
				value={search}
				onChange={event => setSearch(event.target.value)}
			/>
			<div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
				<Button
					type="link"
					size="small"
					icon={<CheckOutlined />}
					style={{ padding: 0 }}
					onClick={() => onChange(options.map(option => option.value))}
				>
					Select all ({options.length})
				</Button>
				<Button
					type="link"
					size="small"
					danger
					icon={<CloseOutlined />}
					style={{ padding: 0 }}
					disabled={selected.length === 0}
					onClick={() => onChange([])}
				>
					Clear all
				</Button>
			</div>
			<Divider style={{ margin: "6px 0" }} />
			<div style={{ maxHeight: 240, overflowY: "auto" }}>
				{visibleOptions.length === 0 && (
					<div style={{ padding: "6px 4px", fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>
						No matches
					</div>
				)}
				{visibleOptions.map(option => {
					const checked = selected.includes(option.value);
					return (
						<div key={option.value} style={{ padding: "3px 4px" }}>
							<Checkbox
								checked={checked}
								onChange={event => toggleValue(option.value, event.target.checked)}
							>
								{option.label}
							</Checkbox>
						</div>
					);
				})}
			</div>
			<Divider style={{ margin: "6px 0" }} />
			<div style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>
				{selected.length === 0
					? `None selected — showing all ${options.length}`
					: `${selected.length} of ${options.length} selected`}
			</div>
		</div>
	);
};

interface AnalyticsFilterBarProps {
	platforms: AnalyticsValidationRecord[];
	channels: AnalyticsValidationRecord[];
	territories: AnalyticsValidationRecord[];
	selection: AnalyticsFilterSelection;
	onChange: (dimension: FilterDimension, values: string[]) => void;
	expanded: boolean;
	onExpand: () => void;
}

const AnalyticsFilterBar: React.FC<AnalyticsFilterBarProps> = ({
	platforms,
	channels,
	territories,
	selection,
	onChange,
	expanded,
	onExpand,
}) => {
	const toOptions = (records: AnalyticsValidationRecord[]): SelectOption[] =>
		records.map(record => ({ value: record.value, label: record.ui_label || record.value }));

	const dimensions = useMemo(
		() => [
			{
				key: "platforms" as FilterDimension,
				label: "Platforms",
				options: toOptions(platforms),
				selected: selection.platforms,
			},
			{
				key: "channels" as FilterDimension,
				label: "Channels",
				options: toOptions(channels),
				selected: selection.channels,
			},
			{
				key: "territories" as FilterDimension,
				label: "Territories",
				options: toOptions(territories),
				selected: selection.territories,
			},
		],
		[platforms, channels, territories, selection]
	);

	if (!expanded) {
		return (
			<Bar>
				<Summary>
					{dimensions.map((dimension, index) => (
						<React.Fragment key={dimension.key}>
							{index > 0 && <span className="summary-dot">•</span>}
							<span className="summary-label" style={{ color: DIMENSION_COLOR[dimension.key] }}>
								{dimension.label}
							</span>
							<span>
								{isAllSelected(dimension.selected, dimension.options)
									? "All"
									: `${dimension.selected.length} of ${dimension.options.length}`}
							</span>
						</React.Fragment>
					))}
					<span className="summary-dot">•</span>
					<Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={onExpand}>
						Expand to edit
					</Button>
				</Summary>
			</Bar>
		);
	}

	return (
		<Bar>
			{dimensions.map(dimension => {
				const color = DIMENSION_COLOR[dimension.key];
				const labelFor = (value: string) =>
					dimension.options.find(option => option.value === value)?.label ?? value;
				// Nothing selected or every option selected → one "All" chip instead of a chip per value.
				const allSelected = isAllSelected(dimension.selected, dimension.options);

				return (
					<Row key={dimension.key}>
						<RowLabel $color={color}>{dimension.label}</RowLabel>
						<Chips>
							{allSelected && <Chip $color={color}>All</Chip>}
							{!allSelected && dimension.selected.map(value => (
								<Chip key={value} $color={color}>
									{labelFor(value)}
									<CloseOutlined
										className="chip-close"
										title={`Remove ${labelFor(value)}`}
										onClick={() =>
											onChange(
												dimension.key,
												dimension.selected.filter(item => item !== value)
											)
										}
									/>
								</Chip>
							))}
							<Popover
								trigger="click"
								placement="bottomLeft"
								destroyTooltipOnHide
								content={
									<FilterPopoverContent
										options={dimension.options}
										selected={dimension.selected}
										onChange={values => onChange(dimension.key, values)}
									/>
								}
							>
								<AddChip type="button" title={`Add ${dimension.label.toLowerCase()}`}>
									<PlusOutlined />
								</AddChip>
							</Popover>
							{!allSelected && (
								<ClearLink
									type="button"
									title={`Clear ${dimension.label.toLowerCase()} (show all)`}
									onClick={() => onChange(dimension.key, [])}
								>
									Clear
								</ClearLink>
							)}
						</Chips>
					</Row>
				);
			})}
		</Bar>
	);
};

export default AnalyticsFilterBar;
